<template>
    <vvo v-slot="v" tag="div">
        <section class="sc-section">
            <div class="sc-section-title">
                <h2>
                    {{ $t("스케줄 설정") }}
                    <span v-if="warn" class="sc-warn-mark" :title="$t('확인이 필요합니다')">!</span>
                </h2>
                <button
                    type="button"
                    class="sc-btn sc-btn--outline"
                    :disabled="!hasTarget"
                    :title="hasTarget ? '' : $t('제어 대상을 먼저 지정하세요.')"
                    @click="openNew"
                >
                    + {{ $t("스케줄 설정 추가") }}
                </button>
            </div>

            <div v-if="rows.length === 0" class="sc-meta">
                {{
                    hasTarget
                        ? $t("등록된 스케줄 설정이 없습니다. 스케줄 설정을 추가하세요.")
                        : $t("제어 대상을 먼저 지정하면 스케줄 설정을 추가할 수 있습니다.")
                }}
            </div>
            <div v-for="row in rows" :key="row.index" class="sc-event-edit-row">
                <span class="t">{{ hhmm(row.event.schdHour) || "--:--" }}</span>
                <button type="button" class="n row-btn" :title="$t('클릭하여 편집')" @click="openEdit(row.index)">
                    {{ row.event.schdEventName }}
                </button>
                <button type="button" class="v row-btn" :title="$t('클릭하여 편집')" @click="openEdit(row.index)">
                    {{ row.label || "-" }}
                </button>
                <button
                    type="button"
                    class="sc-btn sc-btn--ghost sc-btn--icon"
                    :aria-label="$t('삭제')"
                    @click="remove(row.index)"
                >
                    <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1.6"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    >
                        <path d="M3 4.5h10M6.5 4.5v-1.5h3v1.5M4.5 4.5l.5 8.5h6l.5-8.5" />
                    </svg>
                </button>
            </div>

            <b-modal modal-class="sch-modal" ref="EventModal" centered @shown="onShown" @hidden="onHidden">
                <template #modal-header>
                    <h5>{{ editIndex === null ? $t("스케줄 설정 추가") : $t("스케줄 설정 편집") }}</h5>
                </template>
                <template #modal-footer>
                    <ModalFooter @confirm="onValidate(v, applyEvent)" @cancel="$refs.EventModal.hide()" />
                </template>
                <ScheduleSettingDetailPanel
                    ref="EventPanel"
                    :events="detail.events"
                    :targets="detail.targets"
                    :mode="editIndex === null ? 'new' : 'edit'"
                />
            </b-modal>
        </section>
    </vvo>
</template>

<script>
import ModalFooter from "../ModalFooter.vue";
import ScheduleSettingDetailPanel from "../../panels/ScheduleSettingDetailPanel.vue";
import { resolveEventLabels } from "../../utils/scheduleApi";
import { hhmm } from "../../utils/scheduleFormat";

export default {
    components: { ModalFooter, ScheduleSettingDetailPanel },
    props: {
        detail: { type: Object, required: true },
        warn: { type: Boolean, default: false },
    },
    data() {
        return {
            labels: [],
            editIndex: null,
            pending: null, // 모달이 열린 뒤 패널에 주입할 이벤트 (null = 신규)
        };
    },
    computed: {
        /** 제어 대상(설비 유형)이 하나라도 지정되어야 스케줄 설정을 추가할 수 있다 */
        hasTarget() {
            return this.detail.targets.length > 0;
        },
        rows() {
            return this.detail.events
                .map((event, index) => ({ event, index, label: this.labels[index] }))
                .sort((a, b) => hhmm(a.event.schdHour).localeCompare(hhmm(b.event.schdHour)));
        },
    },
    watch: {
        "detail.events": {
            immediate: true,
            deep: true,
            async handler(events) {
                const labels = await resolveEventLabels(events);
                if (events === this.detail.events) this.labels = labels;
            },
        },
    },
    methods: {
        hhmm,
        openNew() {
            this.editIndex = null;
            this.pending = null;
            this.$refs.EventModal.show();
        },
        openEdit(index) {
            this.editIndex = index;
            // 취소해도 원본이 바뀌지 않도록 복사본으로 편집한다
            this.pending = JSON.parse(JSON.stringify(this.detail.events[index]));
            this.$refs.EventModal.show();
        },
        onShown() {
            this.$refs.EventPanel.setData(this.pending);
        },
        onHidden() {
            this.editIndex = null;
            this.pending = null;
        },
        remove(index) {
            this.detail.events.splice(index, 1);
        },
        async applyEvent() {
            const panel = this.$refs.EventPanel;
            const event = panel.getData();
            if (this.isEmpty(event)) return;

            if (this.isEmpty(event.schdHour)) {
                this.alertNoti(this.$t("설정 시간을 입력하세요."));
                return;
            }
            const duplicated = this.detail.events.some(
                (item, i) => i !== this.editIndex && item.schdEventName === event.schdEventName,
            );
            if (duplicated) {
                this.alertNoti(this.$t("같은 이름의 이벤트가 이미 있습니다."));
                return;
            }

            // 현재 선택된 제어 모드의 속성값으로 ctrlCommand 를 다시 만든다
            if (!this.isEmpty(panel.property)) {
                const command = {};
                panel.property.forEach((prop, i) => {
                    command[prop.propCode] = panel.propValue[i];
                });
                event.ctrlCommand = command;
            } else if (typeof event.ctrlCommand === "string") {
                try {
                    event.ctrlCommand = JSON.parse(event.ctrlCommand);
                } catch (e) {
                    event.ctrlCommand = {};
                }
            }

            if (this.editIndex === null) this.detail.events.push(event);
            else this.$set(this.detail.events, this.editIndex, event);
            this.$refs.EventModal.hide();
        },
    },
};
</script>
